// src/components/GroupsManagementModal.jsx
import React, { useState, useEffect } from 'react';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';

export default function GroupsManagementModal({ companyId, onClose }) {
  const [groups, setGroups] = useState(['']);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadGroups();
  }, [companyId]);

  async function loadGroups() {
    if (!companyId) return;
    
    try {
      const companyDoc = await getDoc(doc(db, 'companies', companyId));
      if (companyDoc.exists()) {
        const companyData = companyDoc.data();
        const existingGroups = companyData.groups || [];
        setGroups(existingGroups.length > 0 ? [...existingGroups, ''] : ['']);
      }
    } catch (err) {
      console.error('Error loading groups:', err);
      setError('Failed to load groups');
    } finally {
      setLoading(false);
    }
  }

  function handleGroupChange(index, value) {
    const newGroups = [...groups];
    newGroups[index] = value;
    setGroups(newGroups);
  }

  function handleAddGroup() {
    setGroups([...groups, '']);
  }

  function handleRemoveGroup(index) {
    if (groups.length <= 1) {
      setGroups(['']);
    } else {
      setGroups(groups.filter((_, i) => i !== index));
    }
  }

  async function handleSave() {
    if (!companyId) return;
    
    setSaving(true);
    setError('');

    try {
      // Filter out empty groups and trim whitespace
      const validGroups = groups
        .map(g => g.trim())
        .filter(g => g !== '');

      // Update company document with groups
      const companyRef = doc(db, 'companies', companyId);
      await updateDoc(companyRef, {
        groups: validGroups,
        updatedAt: new Date()
      });

      onClose();
    } catch (err) {
      console.error('Error saving groups:', err);
      setError('Failed to save groups. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
          <div className="text-center">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full text-gray-900 dark:text-white">
        {/* Header */}
        <div className="border-b border-gray-200 p-6 flex justify-between items-center dark:border-gray-700">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">Manage Groups/Sites</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
          >
            ✕
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded dark:bg-red-900/20 dark:border-red-800 dark:text-red-400">
              {error}
            </div>
          )}

          <p className="text-sm text-gray-600 dark:text-gray-400">
            Organize users by location, department, or site (e.g., "London Office", "Manchester Site")
          </p>

          <div className="space-y-2 max-h-64 overflow-y-auto">
            {groups.map((group, index) => (
              <div key={index} className="flex gap-2">
                <input
                  type="text"
                  value={group}
                  onChange={(e) => handleGroupChange(index, e.target.value)}
                  placeholder={`Group ${index + 1}`}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 placeholder:text-gray-400 dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder:text-gray-400"
                  disabled={saving}
                />
                <button
                  type="button"
                  onClick={() => handleRemoveGroup(index)}
                  className="px-3 py-2 text-red-600 hover:bg-red-50 rounded border border-red-200 dark:border-red-800 dark:hover:bg-red-900/20"
                  disabled={saving}
                >
                  Remove
                </button>
              </div>
            ))}
          </div>

          <button
            type="button"
            onClick={handleAddGroup}
            className="text-sm text-blue-600 hover:text-blue-700 font-medium dark:text-blue-400"
            disabled={saving}
          >
            + Add Another Group
          </button>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 dark:bg-blue-900/20 dark:border-blue-800">
            <p className="text-sm text-blue-700 dark:text-blue-400">
              💡 <strong>Tip:</strong> Groups help you organize users and control check access. Assign users to groups in the User Management section.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t p-6 flex gap-3 dark:border-gray-700">
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded font-medium disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
          <button
            onClick={onClose}
            disabled={saving}
            className="flex-1 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 py-2 px-4 rounded font-medium text-gray-700 dark:text-white"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
